import type { Location, NavigationType } from 'react-router-dom'

interface HistoryEntry {
  key: string
  pathname: string
}

/**
 * Mirror of the browser history stack, as far as this app session has seen
 * it: one entry per react-router history key, in stack order.
 *
 * react-router only exposes the current entry, but a detail page's "back to
 * overview" button needs to know whether going back lands on that overview
 * (restoring its persisted filters and scroll offset, see useListState) or
 * somewhere else. useScrollRestoration feeds this on every navigation.
 *
 * Entries created before a full page reload are unknown; a POP onto one of
 * those is recorded as if it were the newest entry, so "previous" is only
 * reliable for navigation that happened in this app session.
 */
const stack: HistoryEntry[] = []
let index = -1

export function recordHistoryEntry(location: Pick<Location, 'key' | 'pathname'>, navigationType: NavigationType): void {
  // Same entry again (StrictMode re-running the effect): nothing changed.
  if (stack[index]?.key === location.key)
    return

  const entry: HistoryEntry = { key: location.key, pathname: location.pathname }

  if (navigationType === 'REPLACE' && index >= 0) {
    stack[index] = entry
    return
  }

  if (navigationType === 'POP') {
    const known = stack.findIndex(e => e.key === location.key)
    if (known >= 0) {
      index = known
      return
    }
  }

  // PUSH, or an entry this session has not seen before (initial load, reload):
  // it becomes the top of the stack, discarding any forward entries.
  stack.splice(index + 1)
  stack.push(entry)
  index = stack.length - 1
}

/** Pathname of the history entry directly behind the current one, if this session saw it. */
export function previousHistoryPathname(): string | undefined {
  return index > 0 ? stack[index - 1].pathname : undefined
}

/** Test-only: forget everything recorded so far. */
export function resetHistoryStack(): void {
  stack.length = 0
  index = -1
}
